import { useNavigate } from "@tanstack/react-router";
import { Building2, PhoneCall, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import type { DriverDto, UpdateProfileInput } from "@mandoob/shared";

import { useUpdateProfile } from "@/api/session";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/language";
import { errorMessage } from "@/lib/errors";
import { normalizePhoneInput } from "@/lib/phone-utils";
import { cn } from "@/lib/utils";
import { loadSession, useSession } from "@/session";

// First-run profile setup: three optional questions (name, company, calling
// phone), each skippable. A localStorage flag keeps it from ever nagging again.

const ONBOARDED_KEY = "mandoob:onboarded";

export function hasOnboarded(): boolean {
  try {
    return !!localStorage.getItem(ONBOARDED_KEY);
  } catch {
    return true; // storage unavailable — never trap the user in onboarding
  }
}

export function markOnboarded() {
  try {
    localStorage.setItem(ONBOARDED_KEY, "1");
  } catch {
    // storage unavailable
  }
}

/** Show onboarding after login while the profile is still bare (and not dismissed). */
export function needsOnboarding(driver: DriverDto): boolean {
  return !driver.company && !driver.callingPhone && !hasOnboarded();
}

type StepId = "name" | "company" | "phone";

const STEPS: {
  id: StepId;
  icon: typeof User;
  titleKey: string;
  hintKey: string;
}[] = [
  {
    id: "name",
    icon: User,
    titleKey: "Mandoob:Onboarding.NameTitle",
    hintKey: "Mandoob:Onboarding.NameHint",
  },
  {
    id: "company",
    icon: Building2,
    titleKey: "Mandoob:Onboarding.CompanyTitle",
    hintKey: "Mandoob:Onboarding.CompanyHint",
  },
  {
    id: "phone",
    icon: PhoneCall,
    titleKey: "Mandoob:Onboarding.PhoneTitle",
    hintKey: "Mandoob:Onboarding.PhoneHint",
  },
];

export function OnboardingPage() {
  const { t, dir } = useLanguage();
  const navigate = useNavigate();
  const session = useSession();
  const driver = session?.driver;

  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState<Record<StepId, string>>({
    name: driver?.name ?? "",
    company: driver?.company ?? "",
    phone: driver?.callingPhone ?? "",
  });
  const [skipped, setSkipped] = useState<Record<StepId, boolean>>({
    name: false,
    company: false,
    phone: false,
  });
  const [phoneError, setPhoneError] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const updateProfile = useUpdateProfile();

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;
  const StepIcon = step.icon;

  const goHome = () => void navigate({ to: "/home", replace: true });

  const finish = async (finalSkipped: Record<StepId, boolean>) => {
    if (finishing) return;
    setFinishing(true);

    const payload: UpdateProfileInput = {};
    const name = values.name.trim();
    if (!finalSkipped.name && name && name !== (driver?.name ?? "")) {
      payload.name = name;
    }
    const company = values.company.trim();
    if (!finalSkipped.company && company) {
      payload.company = company;
    }
    const phone = values.phone.trim();
    if (!finalSkipped.phone && phone) {
      // finish() is only reached with a valid (or skipped/empty) phone.
      const normalized = normalizePhoneInput(phone);
      if (normalized) payload.callingPhone = normalized;
    }

    try {
      if (Object.keys(payload).length > 0) {
        await updateProfile.mutateAsync(payload);
        await loadSession().catch(() => undefined);
      }
      markOnboarded();
      goHome();
    } catch (error) {
      setFinishing(false);
      toast.error(errorMessage(error, t));
    }
  };

  const advance = (skipCurrent: boolean) => {
    const nextSkipped = { ...skipped, [step.id]: skipCurrent };
    setSkipped(nextSkipped);
    if (step.id === "phone" && !skipCurrent) {
      const phone = values.phone.trim();
      if (phone && !normalizePhoneInput(phone)) {
        setPhoneError(true);
        return;
      }
    }
    if (isLast) {
      void finish(nextSkipped);
      return;
    }
    setStepIndex(stepIndex + 1);
  };

  const skipAll = () => {
    markOnboarded();
    goHome();
  };

  const inputProps =
    step.id === "phone"
      ? ({
          inputMode: "tel",
          dir: "ltr",
          autoComplete: "tel",
          placeholder: "05xxxxxxxx",
          className: "tw:text-start",
        } as const)
      : step.id === "company"
        ? ({
            inputMode: "text",
            autoComplete: "organization",
            maxLength: 120,
          } as const)
        : ({
            inputMode: "text",
            autoComplete: "name",
            maxLength: 120,
          } as const);

  return (
    <div dir={dir} className="tw:min-h-dvh tw:bg-background">
      <div className="tw:mx-auto tw:flex tw:min-h-dvh tw:max-w-xl tw:flex-col tw:px-6 tw:pb-[calc(24px+env(safe-area-inset-bottom))] tw:pt-[calc(16px+env(safe-area-inset-top))]">
        <header className="tw:flex tw:items-center tw:justify-between">
          <div
            className="tw:flex tw:items-center tw:gap-2"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={STEPS.length}
            aria-valuenow={stepIndex + 1}
            aria-label={t(
              "Mandoob:Onboarding.Progress",
              stepIndex + 1,
              STEPS.length,
            )}
          >
            {STEPS.map((s, i) => (
              <span
                key={s.id}
                className={cn(
                  "tw:h-2 tw:rounded-full tw:transition-all tw:duration-300",
                  i === stepIndex
                    ? "tw:w-6 tw:bg-brand"
                    : i < stepIndex
                      ? "tw:w-2 tw:bg-brand-300"
                      : "tw:w-2 tw:bg-muted",
                )}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={skipAll}
            className="tw:rounded-full tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:text-muted-foreground tw:transition-colors tw:hover:bg-muted"
          >
            {t("Mandoob:Onboarding.SkipAll")}
          </button>
        </header>

        <div
          key={step.id}
          className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-4 tw:text-center tw:animate-in tw:fade-in tw:slide-in-from-bottom-3 tw:duration-300"
        >
          <div className="tw:flex tw:size-20 tw:items-center tw:justify-center tw:rounded-full tw:bg-brand-50">
            <StepIcon className="tw:size-9 tw:text-brand" strokeWidth={1.5} />
          </div>
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <h1 className="tw:m-0 tw:text-xl tw:font-semibold">
              {t(step.titleKey)}
            </h1>
            <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
              {t(step.hintKey)}
            </p>
          </div>
          <div className="tw:flex tw:w-full tw:max-w-sm tw:flex-col tw:gap-1.5">
            <Input
              value={values[step.id]}
              onChange={(event) => {
                setValues((prev) => ({
                  ...prev,
                  [step.id]: event.target.value,
                }));
                setPhoneError(false);
              }}
              aria-invalid={(step.id === "phone" && phoneError) || undefined}
              aria-label={t(step.titleKey)}
              {...inputProps}
            />
            {step.id === "phone" && phoneError && (
              <p className="tw:m-0 tw:text-start tw:text-xs tw:text-destructive">
                {t("Mandoob:Form.InvalidPhone")}
              </p>
            )}
          </div>
        </div>

        <footer className="tw:flex tw:flex-col tw:gap-2">
          <Button
            type="button"
            size="lg"
            onClick={() => advance(false)}
            loading={isLast && finishing}
            className="tw:w-full"
          >
            {isLast
              ? t("Mandoob:Onboarding.Finish")
              : t("Mandoob:Onboarding.Next")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => advance(true)}
            disabled={finishing}
            className="tw:w-full tw:text-muted-foreground"
          >
            {t("Mandoob:Onboarding.Skip")}
          </Button>
        </footer>
      </div>
    </div>
  );
}
